import React, { useEffect, useState } from "react";
import { ActivityIcon, Clock, User } from "@phosphor-icons/react";
import { Spinner } from "@heroui/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { helpdeskPhaseColumns } from "screens/helpdesk/helpdeskPhaseConfig";

interface PhaseHistoryEntry {
  id: number;
  fromPhase: string | null;
  toPhase: string;
  changedAt: string;
  changedBy: string | null;
}

interface PhaseHistoryTimelineProps {
  ticketId: number;
}

export const PhaseHistoryTimeline: React.FC<PhaseHistoryTimelineProps> = ({
  ticketId,
}) => {
  const { session } = useSession();
  const [history, setHistory] = useState<PhaseHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPhaseHistory = async () => {
      if (!session || !ticketId) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `${API_URL}/helpdesk/ticket/${ticketId}/phase-history`,
          { method: "GET" },
          session,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch phase history");
        }

        const data = await response.json();
        setHistory(data);
      } catch (err) {
        console.error("Error fetching phase history:", err);
        setError("Kon fasegeschiedenis niet laden");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhaseHistory();
  }, [ticketId, session]);

  const getPhaseConfig = (phaseName: string) => {
    return helpdeskPhaseColumns.find((col) => col.id === phaseName);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const timeStr = date.toLocaleTimeString("nl-NL", {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (diffDays === 0) {
      return `Vandaag om ${timeStr}`;
    } else if (diffDays === 1) {
      return `Gisteren om ${timeStr}`;
    } else if (diffDays < 7) {
      return `${diffDays} dagen geleden`;
    } else {
      return date.toLocaleDateString("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="sm" />
        <span className="text-neutral-secondary-mid ml-2 text-sm">
          Fasegeschiedenis laden...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4">
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="py-8 text-center">
        <ActivityIcon
          size={32}
          className="text-neutral-secondary-mid mx-auto mb-2 opacity-40"
          weight="duotone"
        />
        <p className="text-neutral-secondary-mid text-sm">
          Nog geen fasewijzigingen
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {history.map((entry, index) => {
        const toPhaseConfig = getPhaseConfig(entry.toPhase);
        const fromPhaseConfig = entry.fromPhase
          ? getPhaseConfig(entry.fromPhase)
          : null;
        const isFirst = index === 0;
        const isLast = index === history.length - 1;

        return (
          <div key={entry.id} className="relative flex gap-3 pb-6">
            {/* Timeline connector line */}
            {!isLast && (
              <div className="from-neutral-primary/20 absolute top-8 bottom-0 left-4 w-0.5 bg-gradient-to-b to-transparent" />
            )}

            {/* Icon circle */}
            <div
              className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${
                isFirst
                  ? "bg-purple-primary/10 border-purple-primary/40"
                  : "border-neutral-primary/20 bg-white"
              }`}
            >
              <ActivityIcon
                weight="duotone"
                size={16}
                className={
                  isFirst ? "text-purple-primary" : "text-neutral-secondary-mid"
                }
              />
            </div>

            {/* Content */}
            <div className="flex-1 pt-0.5">
              <div className="mb-1 flex items-start justify-between gap-2">
                <div className="flex-1">
                  {entry.fromPhase ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded px-2 py-0.5 text-xs ${fromPhaseConfig?.bgColor || "bg-gray-100"} ${fromPhaseConfig?.color || "text-gray-700"} font-medium`}
                      >
                        {fromPhaseConfig?.label || entry.fromPhase}
                      </span>
                      <span className="text-neutral-secondary-mid text-xs">
                        →
                      </span>
                      <span
                        className={`rounded px-2 py-0.5 text-xs ${toPhaseConfig?.bgColor || "bg-gray-100"} ${toPhaseConfig?.color || "text-gray-700"} font-medium`}
                      >
                        {toPhaseConfig?.label || entry.toPhase}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-secondary-mid text-xs">
                        Initiële fase:
                      </span>
                      <span
                        className={`rounded px-2 py-0.5 text-xs ${toPhaseConfig?.bgColor || "bg-gray-100"} ${toPhaseConfig?.color || "text-gray-700"} font-medium`}
                      >
                        {toPhaseConfig?.label || entry.toPhase}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-neutral-secondary-mid flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <Clock size={12} />
                  <span>{formatDate(entry.changedAt)}</span>
                </div>
                {entry.changedBy && (
                  <div className="flex items-center gap-1">
                    <User size={12} />
                    <span>{entry.changedBy}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
